import React, { useEffect, useRef, useState } from "react";
import { Loader2, Mail } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { FetchAllGetInTouchQueries, ClearGetInTouchAction } from "../../Store/Actions/GetInTouchActions";

const Spinner = () => (
  <div className="flex justify-center items-center py-24">
    <Loader2 className="animate-spin text-themeColor" size={32} />
  </div>
);

function formatDate(isoString) {
  if (!isoString) return "—";
  return new Date(isoString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

const MessageCell = ({ message }) => {
  const [hovered, setHovered] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const cellRef = useRef(null);

  const handleMouseEnter = () => {
    if (cellRef.current) {
      const rect = cellRef.current.getBoundingClientRect();
      const popoverWidth = 300;
      const viewportWidth = window.innerWidth;

      let left = rect.left + window.scrollX;
      if (left + popoverWidth > viewportWidth - 16) {
        left = viewportWidth - popoverWidth - 16;
      }

      setPos({ top: rect.bottom + window.scrollY + 6, left });
    }
    setHovered(true);
  };

  return (
    <td
      ref={cellRef}
      className="px-6 py-4 hidden lg:table-cell"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setHovered(false)}
    >
      <span className="text-gray-500 font-popinsRegular whitespace-nowrap overflow-hidden text-ellipsis max-w-[220px] block cursor-default">
        {message || "—"}
      </span>

      {hovered && message && (
        <div
          style={{ top: pos.top, left: pos.left }}
          className="fixed z-[9999] w-[300px] bg-white border border-gray-200 rounded-xl shadow-xl px-4 py-3"
        >
          <p className="text-[10px] font-popinsSemiBold text-gray-400 uppercase tracking-wide mb-1.5">
            Message
          </p>
          <p className="text-sm font-popinsRegular text-gray-700 leading-relaxed whitespace-pre-wrap break-words">
            {message}
          </p>
        </div>
      )}
    </td>
  );
};

const MainGetInTouch = () => {
  const dispatch = useDispatch();
  const { queries, loader, actionError } = useSelector((state) => state.getInTouch);
  const errorHandled = useRef(false);

  useEffect(() => {
    errorHandled.current = false;
    dispatch(FetchAllGetInTouchQueries());
  }, [dispatch]);

  useEffect(() => {
    if (actionError && !errorHandled.current) {
      errorHandled.current = true;
      toast.error(actionError.message || "An error occurred.");
      dispatch(ClearGetInTouchAction());
    }
  }, [actionError, dispatch]);

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-popinsBold">Get In Touch Queries</h1>
        <p className="text-sm font-popinsRegular text-[#61758A] mt-1">
          View contact form submissions from website visitors.
        </p>
      </div>

      {loader ? (
        <Spinner />
      ) : queries.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
          <Mail size={48} className="mb-4 opacity-40" />
          <p className="text-base font-popinsMedium">No queries found</p>
          <p className="text-sm font-popinsRegular mt-1">
            Contact form submissions will appear here.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-6 py-3 text-xs font-popinsSemiBold text-gray-500 uppercase tracking-wide">
                    Name
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-popinsSemiBold text-gray-500 uppercase tracking-wide hidden md:table-cell">
                    Email
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-popinsSemiBold text-gray-500 uppercase tracking-wide">
                    Subject
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-popinsSemiBold text-gray-500 uppercase tracking-wide hidden lg:table-cell">
                    Message
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-popinsSemiBold text-gray-500 uppercase tracking-wide hidden lg:table-cell">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {queries.map((query) => (
                  <tr key={query.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <span className="font-popinsMedium text-gray-800">
                        {query.fullName || "—"}
                      </span>
                    </td>
                    <td className="px-6 py-4 hidden md:table-cell">
                      <span className="text-gray-600 font-popinsRegular">
                        {query.email || "—"}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-popinsRegular text-gray-700 line-clamp-1">
                        {query.subject || "—"}
                      </span>
                    </td>
                    <MessageCell message={query.message} />
                    <td className="px-6 py-4 text-gray-500 font-popinsRegular hidden lg:table-cell">
                      {formatDate(query.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default MainGetInTouch;
